import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api, setToken, clearToken } from '../lib/api';

interface User {
  id: string;
  email: string;
  role: 'super_admin' | 'tenant_owner';
  full_name: string;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (data: { email: string; password: string; full_name: string; desired_site_name: string; motivation?: string }) => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const data = await api.get<{ user: { id: string; email: string }; platformUser: User }>('/auth/me');
      setUser(data.platformUser ? { ...data.platformUser, id: data.user.id } : null);
    } catch {
      clearToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('occiput_token');
    if (!token) { setIsLoading(false); return; }
    fetchUser().finally(() => setIsLoading(false));
  }, [fetchUser]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.post<{ ok: boolean; token: string }>('/auth/login', { email, password }, { skipAuth: true });
    setToken(data.token);
    await fetchUser();
  }, [fetchUser]);

  // Re-check auth when token changes (e.g. set by Login page directly)
  const refreshAuth = useCallback(async () => {
    const token = localStorage.getItem('occiput_token');
    if (token) await fetchUser();
  }, [fetchUser]);

  const logout = useCallback(() => { clearToken(); setUser(null); }, []);

  const register = useCallback(async (data: { email: string; password: string; full_name: string; desired_site_name: string; motivation?: string }) => {
    await api.post('/auth/register', data, { skipAuth: true });
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, login, logout, register, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
